'use client'
import { Button } from '@heroui/button'
import { Link } from '@heroui/link'
import { Spinner } from '@heroui/spinner'
import Image from 'next/image'
import { useEffect, useState } from 'react'

import MomentsOnlyLogo from '@/assets/branding/moment-only-logo.svg'
import MomentsLogo2 from '@/assets/branding/moments-logo-2.svg'
import InstallIllustration from '@/assets/svgs/install-illustration.svg'
import {
  Comment2AiIcon,
  SendIcon2,
  StarIcon,
  Tag2Icon,
} from '@/components/shared'
import { Typography } from '@/components/ui'
import { GoogleSigninButton } from '@/components/widgets'
import { PRIVACY_POLICY_URL, TERMS_URL } from '@/constants'
import { usePWAStore } from '@/store'

import ClearSession from './clear-session'
import { OtpForm } from './otp-form'

const features = [
  {
    title: 'Favorites',
    description:
      'Highlight special moments and keep them in an easy spot to remember them for later.',
    icon: <StarIcon />,
  },
  {
    title: 'Auto Tags',
    description:
      'Your memories automatically get tagged by topics, feelings, locations.',
    icon: <Tag2Icon />,
  },
  {
    title: 'Reflections',
    description:
      'Talk with an AI Therapist to find insights about something you’re struggling.',
    icon: <Comment2AiIcon />,
  },
  {
    title: 'Talk to Your Future',
    description:
      'Write and send letters to your future self. We’ll let you know when it’s time to open them.',
    icon: <SendIcon2 />,
  },
]

enum ScreenVariant {
  Features = 'features',
  Install = 'install',
  Signin = 'signin',
}

const Screen = ({ isOtpSignInEnabled }: { isOtpSignInEnabled: boolean }) => {
  const pwaInstallerRef = usePWAStore((state) => state.installerRef)
  const isPwaLoading = usePWAStore((state) => state.isLoading)
  const [screenVariant, setScreenVariant] = useState<ScreenVariant>(
    ScreenVariant.Features,
  )

  const handleFeatureClick = () => {
    setScreenVariant(ScreenVariant.Install)
  }

  const handleSkipClick = () => {
    setScreenVariant(ScreenVariant.Signin)
  }

  const handleInstallClick = () => {
    if (pwaInstallerRef?.current) {
      pwaInstallerRef.current.showDialog(true)
    }
  }

  useEffect(() => {
    if (pwaInstallerRef?.current) {
      setScreenVariant(ScreenVariant.Features)
    } else {
      setScreenVariant(ScreenVariant.Signin)
    }
  }, [pwaInstallerRef])

  useEffect(() => {
    if (!pwaInstallerRef?.current) return
    const ref = pwaInstallerRef.current

    const handleInstallComplete = () => {
      setScreenVariant(ScreenVariant.Signin)
    }

    pwaInstallerRef.current.addEventListener(
      'pwa-install-success-event',
      handleInstallComplete,
    )

    return () => {
      if (!ref) return
      ref.removeEventListener(
        'pwa-install-success-event',
        handleInstallComplete,
      )
    }
  }, [pwaInstallerRef])

  if (isPwaLoading) {
    return (
      <div className="mx-auto flex h-full min-h-[100svh] w-full max-w-[329px] flex-1 flex-col items-center justify-center gap-12 pt-14">
        <Spinner />
      </div>
    )
  }

  if (screenVariant === ScreenVariant.Features) {
    return (
      <div className="mx-auto flex h-full min-h-[100svh] w-full max-w-[329px] flex-1 flex-col gap-12 pb-20 pt-14">
        <div className="flex flex-col items-center gap-4">
          <Image src={MomentsOnlyLogo} alt="Moments" width={100} />
          <div>
            <Typography
              weight="regular"
              className="text-center font-crimsonPro text-[28px] leading-8"
            >
              Journaling through time
            </Typography>
            <Typography
              size="large"
              weight="regular"
              className="text-center font-outfit leading-7 text-content4-foreground"
            >
              Your personal time capsule
            </Typography>
          </div>
        </div>
        <ul className="flex flex-col gap-6">
          {features.map((feature) => (
            <li key={feature.title} className="flex gap-4">
              <div className="mt-1 text-xl text-secondary-500">
                {feature.icon}
              </div>
              <div className="flex flex-col gap-0.5">
                <Typography
                  size="small"
                  weight="regular"
                  className="font-outfit"
                >
                  {feature.title}
                </Typography>
                <Typography size="small" weight="light" className="font-outfit">
                  {feature.description}
                </Typography>
              </div>
            </li>
          ))}
        </ul>
        <Button
          size="lg"
          variant="solid"
          color="primary"
          className="primary-button self-center"
          onPress={handleFeatureClick}
        >
          Continue
        </Button>
      </div>
    )
  }

  if (screenVariant === ScreenVariant.Install) {
    return (
      <div className="mx-auto flex h-full min-h-[100svh] w-full max-w-[329px] flex-1 flex-col items-center pb-20 pt-[70px]">
        <Image src={InstallIllustration} alt="Install Art" width={240} />
        <Typography
          weight="regular"
          className="mb-4 mt-8 max-w-[240px] text-center font-crimsonPro text-[28px] leading-8"
        >
          Install Moments for easy access!
        </Typography>
        <Typography
          size="small"
          className="mb-4 text-center font-outfit text-content4-foreground"
        >
          You can install Moments as a regular app on your device. Our
          Progressive Web App (PWA) works seamlessly on Chrome and Safari
          browsers.
        </Typography>
        <Typography
          size="small"
          className="mb-12 text-center font-outfit text-content4-foreground"
        >
          Simply follow the instructions in your browser to install and start
          journaling anytime, anywhere!
        </Typography>
        <Button
          size="lg"
          variant="solid"
          color="primary"
          className="primary-button mb-2"
          onPress={handleInstallClick}
        >
          Install Now
        </Button>
        <Button
          size="sm"
          variant="light"
          onPress={handleSkipClick}
          className="text-content4-foreground"
        >
          Skip
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-[100svh] w-full flex-1 flex-col items-center justify-center">
      <Image src={MomentsLogo2} alt="Moments" width={100} className="mb-6" />
      <Typography
        size="small"
        className="mb-14 max-w-[306px] text-center text-content4-foreground"
      >
        Sometimes you just need to slow down, calm your thoughts, and reflect
        about the things you do
      </Typography>
      <Typography
        size="large"
        className="mb-6 text-center text-default-foreground"
        weight="medium"
      >
        Get started
      </Typography>
      {isOtpSignInEnabled && <OtpForm />}
      <GoogleSigninButton />
      <div className="mt-4 flex justify-center gap-4">
        <Link href={PRIVACY_POLICY_URL} isExternal>
          <Typography size="small" className="text-foreground">
            Privacy Policy
          </Typography>
        </Link>
        <Link href={TERMS_URL} isExternal>
          <Typography size="small" className="text-foreground">
            Terms
          </Typography>
        </Link>
      </div>
      <ClearSession />
    </div>
  )
}

export default Screen
